import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-admin';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Container,
  Alert,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ChevronRight,
  Build as BuildIcon,
  AccountTree as WorkflowIcon,
} from '@mui/icons-material';
import { PageTitle } from '../components/PageTitle';

interface ConfigurationItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
}

const enterpriseAdminItems: ConfigurationItem[] = [
  {
    id: 'brand-service-templates',
    title: 'Brand Service Templates',
    description: 'Manage service templates for brand franchisees',
    icon: <BuildIcon />,
    path: '/configuration/brand-service-templates',
  },
  {
    id: 'brand-workflows',
    title: 'Brand Workflows',
    description: 'Define operational workflows and stages for brand franchisees',
    icon: <WorkflowIcon />,
    path: '/configuration/brand-workflows',
  },
];

export const EnterpriseAdminConfigurationList = () => {
  const navigate = useNavigate();
  const { identity, isLoading } = useAuthState();
  const location = useLocation();

  // Also check localStorage as fallback
  const storedUser = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const isEnterpriseAdmin =
    (identity as any)?.isEnterpriseAdmin === true ||
    storedUser?.isEnterpriseAdmin === true;

  // Redirect tenant users to regular configuration
  React.useEffect(() => {
    if (!isLoading && !isEnterpriseAdmin) {
      navigate('/configuration');
    }
  }, [isEnterpriseAdmin, isLoading, navigate]);

  if (!isEnterpriseAdmin) {
    return null;
  }

  return (
    <Box>
      <PageTitle
        title="Enterprise Administration"
        subtitle="Manage brand-level configurations and templates"
        icon={<SettingsIcon />}
      />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            These configurations apply to all franchisees under your brands. Changes
            made here will cascade to franchisee accounts.
          </Typography>
        </Alert>

        <List sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          {enterpriseAdminItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    py: 3,
                    px: 3,
                    '&:hover': {
                      backgroundColor: 'rgba(28, 81, 149, 0.04)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ color: '#1C5195' }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="h6" component="div">
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    }
                  />
                  <ChevronRight sx={{ color: 'text.secondary' }} />
                </ListItemButton>
              </ListItem>
              {index < enterpriseAdminItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Container>
    </Box>
  );
};
